import { ReactNode, memo } from 'react';

import styled from 'styled-components';

import { IconHire } from '@components/icons/hire';
import { IconTrigger } from '@components/icons/trigger';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useGetTravellerJourneySource } from 'api/traveller-journeys/hooks';
import { TravellerJourneySource } from 'api/traveller-journeys/types';
import { TravellerSource } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import {
  JourneyFormTriggerIcon,
  ManualTriggerIcon,
  ScheduledPollingTriggerIcon,
  TriggerCard,
  TriggerIcon,
  WebhookTriggerIcon,
} from '../triggers/common';
import { useExternalFormSidebar } from '../triggers/sidebars/external-form-sidebar';

const ACTION_TYPE = ACTION_TYPES.journey_triggers;

export const Node = memo((node: TravellerJourneyNode<typeof ACTION_TYPE>) => {
  return (
    <NodeContainer node={node} hideTargetHandle hideSourceHandle disableClick>
      <TravellerJourneyTriggers shortId={node.data.travellerJourneyId} />
      <TriggerIcon />
    </NodeContainer>
  );
});

type TriggerConfig = {
  Icon: ReactNode;
  label: string;
  IconRight?: ReactNode;
};

type TravellerJourneyTriggersProps = {
  shortId: string;
};

const TravellerJourneyTriggers = ({ shortId }: TravellerJourneyTriggersProps) => {
  const { t } = useScopedTranslate(locale);
  const travellerSourceQuery = useGetTravellerJourneySource(shortId);
  const showExternalFormSidebar = useExternalFormSidebar();
  const data = travellerSourceQuery.data;

  if (!data || travellerSourceQuery.isLoading) {
    return <Spinner />;
  }

  const sourceInfo = data.sourceInfo;
  const triggerConfigs: Record<TravellerSource, TriggerConfig> = {
    manual: {
      Icon: <ManualTriggerIcon />,
      label: t('manuallyAdded'),
    },
    external_form: {
      Icon: <JourneyFormTriggerIcon />,
      label: t('externalForm'),
    },
    webhook: { Icon: <WebhookTriggerIcon />, label: t('webhook') },
    scheduled_polling: { Icon: <ScheduledPollingTriggerIcon />, label: t('scheduledPolling') },
    added_from_journey: { Icon: <ManualTriggerIcon />, label: t('addedFromJourney') },
    hire: { Icon: <IconHire />, label: t('hire') },
    Hire: { Icon: <IconHire />, label: t('hire') },
  };

  const trigger = triggerConfigs[sourceInfo.source];

  const description = <TriggerDescription sourceInfo={sourceInfo} />;

  const isExternalForm = sourceInfo.source === 'external_form';
  const handleClick = isExternalForm
    ? () => showExternalFormSidebar({ travellerJourneyId: shortId })
    : undefined;

  return (
    <Container>
      <Stack orientation="horizontal" space={2}>
        <IconTrigger color="elementBlue100" />
        <Text font="bold16" text={t('trigger')} />
      </Stack>
      <TriggerCard
        icon={trigger.Icon}
        title={trigger.label}
        description={description}
        iconRight={trigger.IconRight}
        onClick={handleClick}
      />
    </Container>
  );
};

const TriggerDescription = ({
  sourceInfo,
}: {
  sourceInfo: TravellerJourneySource['sourceInfo'];
}) => {
  const { t } = useScopedTranslate(locale);

  switch (sourceInfo.source) {
    case 'manual':
      return (
        <Stack space={0}>
          <Text
            font="regular12"
            color="textSecondary"
            tooltip={sourceInfo.email}
            text={t('addedBy', {
              name: sourceInfo.name ?? '',
            })}
          />
        </Stack>
      );
    case 'external_form':
      return (
        <Stack space={0}>
          <Text font="regular12" color="textSecondary" text={sourceInfo.name} />
          {sourceInfo.submitterName ? (
            <Text
              font="regular12"
              color="textSecondary"
              tooltip={sourceInfo.submitterEmail ?? ''}
              text={t('submittedBy', { name: sourceInfo.submitterName })}
            />
          ) : null}
        </Stack>
      );
    default:
      return null;
  }
};

const Container = styled(Stack).attrs({ space: 4 })({
  width: pxToRem(352),
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  backgroundColor: theme.color.backgroundLightDark,
  padding: pxToRem(16),
  borderRadius: pxToRem(8),
});

const locale = {
  en: {
    title: 'Start event',
    automaticDescription: 'Started automatically for traveller entering journey',
    manualDescription: 'Started manually for traveller entering journey',

    trigger: 'Trigger',
    manuallyAdded: 'Manually added',
    webhook: 'Webhook',
    externalForm: 'External form',
    scheduledPolling: 'Scheduled polling',
    addedFromJourney: 'Added from journey',
    hire: '50.Hire',
    addedBy: 'Added by: {name}',
    submittedBy: 'Submitted by: {name}',
  },
};
